import { FormEvent, useCallback, useEffect, useState } from 'react';
import { ApiError } from '../services/api';
import { adminService } from '../services/admin.service';
import { AdminUser, AdminTemplate, TemplateCreateInput, TemplateUpdateInput } from '../types/admin.types';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Popover } from '../components/ui/Popover';
import { useToast } from '../hooks/useToast';

const CATEGORY_OPTIONS = ['component', 'schema', 'pattern'] as const;
type Category = typeof CATEGORY_OPTIONS[number];

const CATEGORY_COLORS: Record<Category, string> = {
  component: 'bg-blue-100 text-blue-800',
  schema: 'bg-purple-100 text-purple-800',
  pattern: 'bg-amber-100 text-amber-800',
};

function slugify(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

// ─── Template form ──────────────────────────────────────────────────────────

interface TemplateFormState {
  slug: string;
  name: string;
  description: string;
  category: Category;
  content: string;
}

const emptyForm = (): TemplateFormState => ({
  slug: '',
  name: '',
  description: '',
  category: 'component',
  content: '',
});

function fromTemplate(t: AdminTemplate): TemplateFormState {
  return {
    slug: t.slug,
    name: t.name,
    description: t.description,
    category: t.category as Category,
    content: t.content,
  };
}

interface TemplateFormProps {
  form: TemplateFormState;
  onChange: (form: TemplateFormState) => void;
  onSubmit: (e: FormEvent) => void;
  busy: boolean;
  submitLabel: string;
  isEdit: boolean;
}

function TemplateForm({ form, onChange, onSubmit, busy, submitLabel, isEdit }: TemplateFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {!isEdit && (
        <div>
          <label className="block text-xs font-medium text-neutral-700 mb-1">Slug</label>
          <input
            type="text"
            required
            value={form.slug}
            onChange={(e) => onChange({ ...form, slug: e.target.value })}
            className="w-full text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-900/20"
            placeholder="my-template-slug"
          />
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-neutral-700 mb-1">Name</label>
        <input
          type="text"
          required
          value={form.name}
          onChange={(e) => {
            const name = e.target.value;
            onChange({ ...form, name, slug: isEdit ? form.slug : slugify(name) });
          }}
          className="w-full text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-900/20"
          placeholder="Template name"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-700 mb-1">Category</label>
        <select
          value={form.category}
          onChange={(e) => onChange({ ...form, category: e.target.value as Category })}
          className="w-full text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-900/20 bg-white"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-700 mb-1">Description</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          className="w-full text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-900/20"
          placeholder="Short description"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-700 mb-1">Content</label>
        <textarea
          required
          value={form.content}
          onChange={(e) => onChange({ ...form, content: e.target.value })}
          rows={10}
          className="w-full text-sm font-mono border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-900/20 resize-y"
          placeholder="Template content…"
        />
      </div>
      <Button type="submit" className="w-full" disabled={busy}>
        {busy ? 'Saving…' : submitLabel}
      </Button>
    </form>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function AdminPage() {
  const { showToast } = useToast();
  const [secretInput, setSecretInput] = useState('');
  const [adminSecret, setAdminSecret] = useState<string | null>(null);

  // Users state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  // Templates state
  const [templates, setTemplates] = useState<AdminTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AdminTemplate | null>(null);
  const [addForm, setAddForm] = useState<TemplateFormState>(emptyForm());
  const [editForm, setEditForm] = useState<TemplateFormState>(emptyForm());
  const [templateBusy, setTemplateBusy] = useState(false);
  const [busyTemplateId, setBusyTemplateId] = useState<string | null>(null);

  const clearSecret = useCallback((message: string) => {
    setAdminSecret(null);
    setSecretInput('');
    setUsers([]);
    setTemplates([]);
    setError(message);
  }, []);

  const loadUsers = useCallback(async (secret: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const nextUsers = await adminService.listUsers(secret);
      setUsers(nextUsers);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearSecret('Admin access denied. Enter the admin secret again.');
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load users.');
    } finally {
      setIsLoading(false);
    }
  }, [clearSecret]);

  const loadTemplates = useCallback(async (secret: string) => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const list = await adminService.listTemplates(secret);
      setTemplates(list);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearSecret('Admin access denied. Enter the admin secret again.');
        return;
      }
      setTemplatesError(err instanceof Error ? err.message : 'Failed to load templates.');
    } finally {
      setTemplatesLoading(false);
    }
  }, [clearSecret]);

  useEffect(() => {
    if (adminSecret) {
      void loadUsers(adminSecret);
      void loadTemplates(adminSecret);
    }
  }, [adminSecret, loadUsers, loadTemplates]);

  const handleSecretSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!secretInput.trim()) {
      setError('Enter the admin secret.');
      return;
    }
    setAdminSecret(secretInput.trim());
  };

  // ── User actions ────────────────────────────────────────────────────────

  const handleApprove = async (userId: string) => {
    if (!adminSecret) return;
    setBusyUserId(userId);
    setError(null);
    try {
      const approvedUser = await adminService.approveUser(adminSecret, userId);
      setUsers((current) =>
        current.map((user) => (user.id === userId ? approvedUser : user))
      );
      showToast('User approved and notified by email.', 'success');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearSecret('Admin access denied. Enter the admin secret again.');
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to approve user.');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDelete = async (userId: string, email: string) => {
    if (!adminSecret) return;
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    setBusyUserId(userId);
    setError(null);
    try {
      await adminService.deleteUser(adminSecret, userId);
      setUsers((current) => current.filter((user) => user.id !== userId));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearSecret('Admin access denied. Enter the admin secret again.');
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to delete user.');
    } finally {
      setBusyUserId(null);
    }
  };

  // ── Template actions ─────────────────────────────────────────────────────

  const handleAddTemplate = async (e: FormEvent) => {
    e.preventDefault();
    if (!adminSecret) return;
    setTemplateBusy(true);
    try {
      const input: TemplateCreateInput = {
        slug: addForm.slug,
        name: addForm.name,
        description: addForm.description,
        category: addForm.category,
        content: addForm.content,
      };
      const created = await adminService.createTemplate(adminSecret, input);
      setTemplates((prev) => [created, ...prev]);
      setShowAddTemplate(false);
      setAddForm(emptyForm());
      showToast('Template created.', 'success');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearSecret('Admin access denied. Enter the admin secret again.');
        return;
      }
      showToast(err instanceof Error ? err.message : 'Failed to create template.', 'error');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleEditTemplate = async (e: FormEvent) => {
    e.preventDefault();
    if (!adminSecret || !editingTemplate) return;
    setTemplateBusy(true);
    try {
      const input: TemplateUpdateInput = {
        name: editForm.name,
        description: editForm.description,
        category: editForm.category,
        content: editForm.content,
      };
      const updated = await adminService.updateTemplate(adminSecret, editingTemplate.id, input);
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setEditingTemplate(null);
      showToast('Template updated.', 'success');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearSecret('Admin access denied. Enter the admin secret again.');
        return;
      }
      showToast(err instanceof Error ? err.message : 'Failed to update template.', 'error');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleDeactivate = async (templateId: string) => {
    if (!adminSecret) return;
    setBusyTemplateId(templateId);
    try {
      await adminService.deactivateTemplate(adminSecret, templateId);
      setTemplates((prev) =>
        prev.map((t) => (t.id === templateId ? { ...t, is_active: false } : t))
      );
      showToast('Template deactivated.', 'success');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearSecret('Admin access denied. Enter the admin secret again.');
        return;
      }
      showToast(err instanceof Error ? err.message : 'Failed to deactivate template.', 'error');
    } finally {
      setBusyTemplateId(null);
    }
  };

  // ── Secret gate ──────────────────────────────────────────────────────────

  if (!adminSecret) {
    return (
      <div className="min-h-screen bg-neutral-50 px-4 py-16">
        <div className="mx-auto max-w-md rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-neutral-900">Admin access</h1>
          <p className="mt-2 text-sm text-neutral-600">
            Enter the admin secret to manage pending accounts.
          </p>
          <form onSubmit={handleSecretSubmit} className="mt-6 space-y-4">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <Input
              type="password"
              label="Admin secret"
              value={secretInput}
              onChange={(e) => setSecretInput(e.target.value)}
              required
              autoFocus
            />
            <Button type="submit" className="w-full">
              Continue
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 px-4 py-10">
      <div className="mx-auto max-w-5xl space-y-10">

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-neutral-900">Admin panel</h1>
            <p className="mt-2 text-sm text-neutral-600">
              Manage users and templates.
            </p>
          </div>
          <Button variant="secondary" onClick={() => clearSecret('Admin secret cleared.')}>
            Clear secret
          </Button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ── Users ──────────────────────────────────────────────────────── */}
        <section>
          <h2 className="mb-4 text-xl font-semibold text-neutral-900">User approvals</h2>
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Registered at</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-sm text-neutral-500">Loading users...</td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-sm text-neutral-500">No users found.</td>
                  </tr>
                ) : (
                  users.map((user) => {
                    const isBusy = busyUserId === user.id;
                    return (
                      <tr key={user.id}>
                        <td className="px-6 py-4 text-sm text-neutral-900">{user.email}</td>
                        <td className="px-6 py-4 text-sm text-neutral-600">
                          {new Date(user.createdAt).toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${user.isApproved ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            {user.isApproved ? 'Approved' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            {!user.isApproved && (
                              <Button size="sm" onClick={() => void handleApprove(user.id)} disabled={isBusy}>
                                Approve
                              </Button>
                            )}
                            <Button size="sm" variant="danger" onClick={() => void handleDelete(user.id, user.email)} disabled={isBusy}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Templates ──────────────────────────────────────────────────── */}
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-neutral-900">Templates</h2>
            <Button size="sm" onClick={() => { setAddForm(emptyForm()); setShowAddTemplate(true); }}>
              Add template
            </Button>
          </div>

          {templatesError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {templatesError}
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Active</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {templatesLoading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-sm text-neutral-500">Loading templates...</td>
                  </tr>
                ) : templates.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-sm text-neutral-500">No templates found.</td>
                  </tr>
                ) : (
                  templates.map((tmpl) => {
                    const isBusy = busyTemplateId === tmpl.id;
                    return (
                      <tr key={tmpl.id}>
                        <td className="px-6 py-4 text-sm text-neutral-900">{tmpl.name}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${CATEGORY_COLORS[tmpl.category as Category] ?? 'bg-neutral-100 text-neutral-700'}`}>
                            {tmpl.category}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tmpl.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-neutral-100 text-neutral-500'}`}>
                            {tmpl.is_active ? 'yes' : 'no'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => { setEditForm(fromTemplate(tmpl)); setEditingTemplate(tmpl); }}
                              disabled={isBusy}
                            >
                              Edit
                            </Button>
                            {tmpl.is_active && (
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => void handleDeactivate(tmpl.id)}
                                disabled={isBusy}
                              >
                                Deactivate
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Add template popover */}
      <Popover open={showAddTemplate} onClose={() => setShowAddTemplate(false)} title="Add template" size="lg">
        <TemplateForm
          form={addForm}
          onChange={setAddForm}
          onSubmit={handleAddTemplate}
          busy={templateBusy}
          submitLabel="Create template"
          isEdit={false}
        />
      </Popover>

      {/* Edit template popover */}
      <Popover open={editingTemplate !== null} onClose={() => setEditingTemplate(null)} title="Edit template" size="lg">
        <TemplateForm
          form={editForm}
          onChange={setEditForm}
          onSubmit={handleEditTemplate}
          busy={templateBusy}
          submitLabel="Save changes"
          isEdit={true}
        />
      </Popover>
    </div>
  );
}
